// Operational Dashboard Service — real platform APIs only (no mock data)
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import {
  DashboardStats,
  HotelMetrics,
  RecentActivity,
  AlertSummary,
  SystemMetrics,
} from '../types/operational';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['data', 'items', 'results', 'hotels', 'alerts', 'activities']) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }
  return [];
}

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

class OperationalService {
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const [globalRes, occupancyRes, alertsRes] = await Promise.allSettled([
        api.get('/api/v1/admin/dashboard/global-stats'),
        api.get('/api/v1/dashboard/occupancy'),
        api.get('/api/v1/security/alerts', { params: { status: 'active' } }),
      ]);

      const global =
        globalRes.status === 'fulfilled'
          ? (globalRes.value.data?.data || globalRes.value.data || {})
          : {};
      const occupancy =
        occupancyRes.status === 'fulfilled'
          ? (occupancyRes.value.data?.data || occupancyRes.value.data || {})
          : {};
      const alerts =
        alertsRes.status === 'fulfilled' ? asArray<Record<string, unknown>>(alertsRes.value.data) : [];

      return {
        totalHotels: num(global.total_hotels ?? global.totalHotels ?? global.hotel_count),
        activeGuests: num(global.active_guests ?? global.activeGuests ?? occupancy.current_guests ?? occupancy.active_guests),
        pendingAlerts: num(global.pending_alerts ?? global.pendingAlerts, alerts.length),
        documentsToValidate: num(global.documents_to_validate ?? global.documentsToValidate),
        totalRevenue: num(global.total_revenue ?? global.totalRevenue),
        occupancyRate: num(occupancy.occupancy_rate ?? occupancy.occupancyRate ?? global.occupancy_rate),
      };
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      return {
        totalHotels: 0,
        activeGuests: 0,
        pendingAlerts: 0,
        documentsToValidate: 0,
        totalRevenue: 0,
        occupancyRate: 0,
      };
    }
  }

  async getHotelMetrics(): Promise<HotelMetrics[]> {
    try {
      const [hotelsRes, statsRes] = await Promise.allSettled([
        api.get('/api/v1/admin/hotels', { params: { limit: 50 } }),
        api.get('/api/v1/admin/hotels/stats'),
      ]);

      const hotels = hotelsRes.status === 'fulfilled'
        ? asArray<Record<string, unknown>>(hotelsRes.value.data)
        : [];
      const statsPayload =
        statsRes.status === 'fulfilled'
          ? (statsRes.value.data?.data || statsRes.value.data || {})
          : {};
      const byHotel = Array.isArray(statsPayload?.by_hotel)
        ? (statsPayload.by_hotel as Record<string, unknown>[])
        : asArray<Record<string, unknown>>(statsPayload);

      const statsMap = new Map<string, Record<string, unknown>>();
      byHotel.forEach((row) => {
        const id = String(row.hotel_id || row.id || '');
        if (id) statsMap.set(id, row);
      });

      return hotels.map((hotel) => {
        const id = String(hotel.id || hotel.hotel_id || '');
        const stats = statsMap.get(id) || {};
        return {
          hotelId: id,
          hotelName: String(hotel.name || hotel.hotel_name || 'Hôtel'),
          occupancyRate: num(stats.occupancy_rate ?? hotel.occupancy_rate),
          totalGuests: num(stats.active_guests ?? stats.total_guests ?? hotel.active_guests),
          activeAlerts: num(stats.active_alerts ?? hotel.active_alerts),
          revenue: num(stats.revenue ?? hotel.revenue),
          lastUpdate: String(stats.updated_at || hotel.updated_at || new Date().toISOString()),
        };
      });
    } catch (error) {
      console.error('Failed to fetch hotel metrics:', error);
      return [];
    }
  }

  async getRecentActivity(): Promise<RecentActivity[]> {
    try {
      const response = await api.get('/api/v1/security/alerts', { params: { limit: 20 } });
      const alerts = asArray<Record<string, unknown>>(response.data);
      return alerts.map((alert, index) => ({
        id: String(alert.id || `alert-${index}`),
        type: 'alert' as const,
        description: String(alert.message || alert.description || alert.title || 'Alerte sécurité'),
        hotelName: String(alert.hotel_name || alert.hotelName || 'Hôtel'),
        timestamp: String(alert.created_at || alert.timestamp || new Date().toISOString()),
        severity: (String(alert.severity || 'medium').toLowerCase() as RecentActivity['severity']),
      }));
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      return [];
    }
  }

  async getActiveAlerts(): Promise<AlertSummary[]> {
    try {
      const response = await api.get('/api/v1/security/alerts', { params: { status: 'active' } });
      const alerts = asArray<Record<string, unknown>>(response.data);
      return alerts.map((alert, index) => ({
        id: String(alert.id || `active-alert-${index}`),
        type: String(alert.type || alert.alert_type || 'ALERT'),
        severity: (String(alert.severity || 'medium').toLowerCase() as AlertSummary['severity']),
        description: String(alert.message || alert.description || alert.title || 'Alerte'),
        hotelName: String(alert.hotel_name || alert.hotelName || 'Hôtel'),
        guestName: alert.guest_name ? String(alert.guest_name) : undefined,
        timestamp: String(alert.created_at || alert.timestamp || new Date().toISOString()),
        status: (String(alert.status || 'active').toLowerCase() as AlertSummary['status']),
      }));
    } catch (error) {
      console.error('Failed to fetch active alerts:', error);
      return [];
    }
  }

  async getSystemMetrics(): Promise<SystemMetrics> {
    // No public CNS infra metrics endpoint — return zeros rather than fabricated load numbers.
    try {
      const response = await api.get('/health');
      const ok = response.status === 200;
      return {
        cpuUsage: 0,
        memoryUsage: 0,
        diskUsage: 0,
        networkLatency: 0,
        activeConnections: ok ? 1 : 0,
        requestsPerMinute: 0,
      };
    } catch {
      return {
        cpuUsage: 0,
        memoryUsage: 0,
        diskUsage: 0,
        networkLatency: 0,
        activeConnections: 0,
        requestsPerMinute: 0,
      };
    }
  }
}

export const operationalService = new OperationalService();
